// -------------------------------------------------------------------------
// Accès hors ligne aux formations achetées.
//
// Principe : quand l'utilisateur est connecté et en ligne, on synchronise en
// tache de fond le contenu de ses formations (structure + leçons + images)
// dans IndexedDB. Ensuite, les lectures de contenu passent par apiGetOffline()
// qui retombe sur le cache local si le réseau manque. Les images passent par
// le composant OfflineImage.
//
// Volontairement isolé du reste du code : rien à importer ailleurs à part
// apiGetOffline (lectures) et syncEnrolledCourses (déclenché à la connexion).
// -------------------------------------------------------------------------
import { api } from "./api";

const DB_NAME = "sflearning-offline";
const DB_VERSION = 1;
const STORE_JSON = "json";
const STORE_MEDIA = "media";

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexeddb-unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_JSON)) db.createObjectStore(STORE_JSON);
      if (!db.objectStoreNames.contains(STORE_MEDIA)) db.createObjectStore(STORE_MEDIA);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function idbGet(store, key) {
  return openDB()
    .then((db) => new Promise((resolve, reject) => {
      const r = db.transaction(store, "readonly").objectStore(store).get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    }))
    .catch(() => undefined);
}

function idbSet(store, key, val) {
  return openDB()
    .then((db) => new Promise((resolve, reject) => {
      const t = db.transaction(store, "readwrite");
      t.objectStore(store).put(val, key);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    }))
    .catch(() => {});
}

// ---- JSON (contenu des formations) ----
function cacheJson(key, data) {
  if (data === undefined || data === null) return;
  idbSet(STORE_JSON, key, data);
}
function getJson(key) {
  return idbGet(STORE_JSON, key);
}

/**
 * GET avec repli hors ligne. En ligne : renvoie la réponse réseau et met en
 * cache. Hors ligne (ou erreur réseau) : renvoie la version en cache si elle
 * existe, en cherchant aussi la clé sans query string.
 * Renvoie un objet façon axios ({ data }).
 */
export async function apiGetOffline(url) {
  try {
    const r = await api.get(url);
    cacheJson(url, r.data);
    return r;
  } catch (e) {
    let cached = await getJson(url);
    if (cached === undefined && url.includes("?")) {
      cached = await getJson(url.split("?")[0]);
    }
    if (cached !== undefined && cached !== null) {
      return { data: cached, fromCache: true };
    }
    throw e;
  }
}

// ---- Médias (images) ----
const _blobUrls = new Map(); // url réseau -> objectURL local

export async function offlineMediaUrl(url) {
  if (!url) return null;
  if (_blobUrls.has(url)) return _blobUrls.get(url);
  const blob = await idbGet(STORE_MEDIA, url);
  if (blob) {
    const objectUrl = URL.createObjectURL(blob);
    _blobUrls.set(url, objectUrl);
    return objectUrl;
  }
  return null;
}

export async function cacheMedia(url) {
  if (!url || !/^https?:\/\//.test(url)) return;
  const existing = await idbGet(STORE_MEDIA, url);
  if (existing) return;
  try {
    const resp = await fetch(url, { mode: "cors" });
    if (!resp.ok) return;
    const blob = await resp.blob();
    if (blob && blob.size > 0) await idbSet(STORE_MEDIA, url, blob);
  } catch (e) {
    // pas de CORS ou hors ligne : on ignore, l'image reste dispo en ligne.
  }
}

// ---- Synchronisation des formations achetées ----
let _syncing = false;

function collectMediaFromBlocks(blocks, bag) {
  (blocks || []).forEach((b) => {
    const u = b && b.content && b.content.url;
    if (u && /^https?:\/\//.test(u)) bag.add(u);
  });
}

/**
 * Télécharge en local le contenu de toutes les formations de l'utilisateur.
 * Silencieux : toute erreur (hors ligne, non connecté) est avalée.
 */
export async function syncEnrolledCourses() {
  if (_syncing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  if (typeof localStorage === "undefined" || !localStorage.getItem("sf_token")) return;
  _syncing = true;
  const media = new Set();
  try {
    const enr = await api.get("/enrollments/my");
    cacheJson("/enrollments/my", enr.data);
    const enrollments = Array.isArray(enr.data) ? enr.data : [];

    for (const e of enrollments) {
      const baseCourse = (e && e.course) || e || {};
      const courseId = baseCourse.course_id;
      if (baseCourse.cover_image) media.add(baseCourse.cover_image);
      if (!courseId) continue;

      let full = baseCourse;
      try {
        const r = await api.get(`/courses/${courseId}`);
        cacheJson(`/courses/${courseId}`, r.data);
        full = r.data || baseCourse;
      } catch (_) { /* garde la version de l'enrollment */ }
      if (full.cover_image) media.add(full.cover_image);

      const modules = full.modules || baseCourse.modules || [];
      for (const m of modules) {
        for (const lesson of (m.lessons || [])) {
          const lid = lesson && lesson.lesson_id;
          if (!lid) continue;
          try {
            const lr = await api.get(`/lessons/${lid}`);
            cacheJson(`/lessons/${lid}`, lr.data);
            collectMediaFromBlocks(lr.data && lr.data.blocks, media);
          } catch (_) { /* leçon non accessible : on saute */ }
        }
      }
    }

    for (const url of media) {
      await cacheMedia(url);
    }
  } catch (e) {
    // hors ligne ou non autorisé : on ne fait rien.
  } finally {
    _syncing = false;
  }
}

/** Vide tout le cache hors ligne (utile pour un futur écran de réglages). */
export async function clearOfflineData() {
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const t = db.transaction([STORE_JSON, STORE_MEDIA], "readwrite");
      t.objectStore(STORE_JSON).clear();
      t.objectStore(STORE_MEDIA).clear();
      t.oncomplete = resolve;
      t.onerror = resolve;
    });
    _blobUrls.forEach((u) => URL.revokeObjectURL(u));
    _blobUrls.clear();
  } catch (e) { /* ignore */ }
}
